import { useState, useEffect } from "react";
import type * as themes from "../themes";

export function useLocalStorage<T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const storedValue = window.localStorage.getItem(key);
    try {
      return storedValue !== null ? (JSON.parse(storedValue) as T) : defaultValue;
    } catch (error) {
      console.error(error);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  }, [key, value]);

  return [value, setValue];
}

class StorageEvens extends EventTarget {
  events = {
    SETTINGS_UPDATED: "SETTINGS_UPDATED",
  };
  onUpdate(v: typeof defaultSettings) {
    this.dispatchEvent(new CustomEvent(this.events.SETTINGS_UPDATED, { detail: v }));
  }
}
const storageEvents = new StorageEvens();

const defaultSettings = {
  hand: "right" as "right" | "left",
  xmas: true,
  xmas_flake: 20, // the amount of snowflakes
  theme: "dark" as keyof typeof themes,
  dev_mode: false,
  highlight_movies: true,
  highlight_favs: true,
  highlight_watching: true,
};
export type LocalSettings = typeof defaultSettings;
export function useLocalSettings() {
  const [settings, setValue] = useLocalStorage("l_settings", defaultSettings);

  const setSettings = (v: LocalSettings) => storageEvents.onUpdate(v);
  // kinda bad because if multiple components are using useLocalSettings
  // this event will fire in every component that's using it
  // and will call localStorage.setItem in each component.
  // it's ok for now since it shouldn't be used by many components at a time.

  useEffect(() => {
    const handleSettingChange = (evt: any) => setValue(evt.detail);
    storageEvents.addEventListener(storageEvents.events.SETTINGS_UPDATED, handleSettingChange);

    // Merge missing setting keys with defaultSettings
    // TODO: delete values no longer in defaultSettings?
    for (const defaultSettingsKey of Object.keys(defaultSettings)) {
      if (!Object.prototype.hasOwnProperty.call(settings, defaultSettingsKey)) {
        setSettings({ ...defaultSettings, ...settings });
        break;
      }
    }

    return () => {
      storageEvents.removeEventListener(storageEvents.events.SETTINGS_UPDATED, handleSettingChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { settings, setSettings };
}
